import { z } from "zod";

import { CURRENCIES, type AuditChange } from "./organizations";
import { PAYMENT_TERM_LABELS, paymentTermsSchema } from "./payment-terms";

// A person's contracts: every employment contract and contractor agreement People & HR keeps for someone, each one the
// terms that hold from the day it takes effect. A raise or an annex is a new contract rather than a change to the old
// one, so the history stays: the contract in effect is the newest whose day has come (`currentContract`), and one whose
// day has not come yet is scheduled. The fields are what EU Directive 2019/1152 (Article 4(2)) says an employer must
// tell a worker in writing — where they work, how long the contract runs, probation, paid leave, notice and pay — and
// what a contractor agreement adds: whom the contractor invoices through and on what terms. A person's contracts are
// kept on their own record (`./people.ts`), which is why this module imports nothing from it.

/** What kind of agreement a contract is. A person's type says what they are now; a contract says what it was. */
export const CONTRACT_KINDS = ["employment", "contractor"] as const;
export type ContractKind = (typeof CONTRACT_KINDS)[number];

export const CONTRACT_KIND_LABELS: Readonly<Record<ContractKind, string>> = {
	employment: "Employment contract",
	contractor: "Contractor agreement",
};

/** How long an employment contract runs: until somebody ends it, or to a day it names. */
export const CONTRACT_TERMS = ["open-ended", "fixed-term"] as const;
export type ContractTerm = (typeof CONTRACT_TERMS)[number];

export const CONTRACT_TERM_LABELS: Readonly<Record<ContractTerm, string>> = {
	"open-ended": "Open-ended",
	"fixed-term": "Fixed-term",
};

/** Whether an employment contract is full-time or part-time, which the contract states rather than the hours imply. */
export const WORKING_TIMES = ["full-time", "part-time"] as const;
export type WorkingTime = (typeof WORKING_TIMES)[number];

export const WORKING_TIME_LABELS: Readonly<Record<WorkingTime, string>> = {
	"full-time": "Full-time",
	"part-time": "Part-time",
};

/** Where the work is done: at the organization's premises, partly there, or wherever the person is. */
export const WORKPLACES = ["on-site", "hybrid", "remote"] as const;
export type Workplace = (typeof WORKPLACES)[number];

export const WORKPLACE_LABELS: Readonly<Record<Workplace, string>> = {
	"on-site": "On site",
	hybrid: "Hybrid",
	remote: "Remote",
};

/** What an amount of pay is paid for. */
export const PAY_UNITS = ["hour", "day", "month", "year"] as const;
export type PayUnit = (typeof PAY_UNITS)[number];

/** What the form's Per select calls each unit. */
export const PAY_UNIT_LABELS: Readonly<Record<PayUnit, string>> = {
	hour: "Hour",
	day: "Day",
	month: "Month",
	year: "Year",
};

/** How an amount reads with its unit: "EUR 4,200 a month". */
const PAY_UNIT_PHRASES: Readonly<Record<PayUnit, string>> = {
	hour: "an hour",
	day: "a day",
	month: "a month",
	year: "a year",
};

/** The most a contract may pay in one unit: a guard against a slipped decimal point, not a policy. */
export const PAY_AMOUNT_MAX = 10_000_000;

/** The most contracts one person keeps: a new one a quarter for ten years. They ride on the person's own record. */
export const PERSON_CONTRACTS_MAX = 40;

/**
 * A day in a contract, as `YYYY-MM-DD`. A second declaration of `calendarDateSchema` rather than an import:
 * `./people.ts` keeps a person's contracts on their record and so imports this module, and the seam only runs that way.
 */
const contractDateSchema = z.iso.date();

/** An amount of pay: above nothing, at most `PAY_AMOUNT_MAX`, in cents at the finest. */
export const payAmountSchema = z
	.number("Enter an amount")
	.positive("Enter an amount above nothing")
	.max(PAY_AMOUNT_MAX, "Enter at most 10,000,000")
	.multipleOf(0.01, "Use at most two decimals");

/** What a contract pays, before tax: an amount, the currency it is paid in, and what it is paid for. */
export const paySchema = z.object({
	amount: payAmountSchema,
	/** One of the currencies the company identity offers; it need not be the company's own. */
	currency: z.enum(CURRENCIES, "Choose a currency"),
	unit: z.enum(PAY_UNITS, "Choose what the amount is for"),
});
export type Pay = z.infer<typeof paySchema>;

/** The hours an employment contract sets for a week: at least one, at most 80, to two decimals at the finest. */
export const weeklyHoursSchema = z
	.number("Enter the hours")
	.min(1, "Enter at least 1 hour")
	.max(80, "Enter at most 80 hours")
	.multipleOf(0.01, "Use at most two decimals");

/** Days of notice either side gives. */
export const noticeDaysSchema = z.int("Enter a whole number of days").min(0).max(365, "Enter at most 365 days");

/** Days of paid leave a year. */
export const leaveDaysSchema = z.int("Enter a whole number of days").min(0).max(100, "Enter at most 100 days");

/** What every contract carries, whatever its kind. */
const contractFields = {
	/** The day these terms take effect: a new contract's first day, or the day an annex changes them. */
	effectiveFrom: contractDateSchema,
	/**
	 * The last day, inclusive: a fixed-term contract's end, or an agreement's. Absent while it runs on — an open-ended
	 * employment contract never has one; when somebody leaves, it is their own end date that says so.
	 */
	endsOn: contractDateSchema.optional(),
	workplace: z.enum(WORKPLACES, "Choose where they work"),
	/** Days of notice either side gives; absent when the contract leaves it to the law. */
	noticeDays: noticeDaysSchema.optional(),
};

/** What only an employment contract carries. */
const employmentFields = {
	kind: z.literal("employment"),
	term: z.enum(CONTRACT_TERMS, "Choose open-ended or fixed-term"),
	workingTime: z.enum(WORKING_TIMES, "Choose full-time or part-time"),
	weeklyHours: weeklyHoursSchema,
	/** The last day of the probation, inclusive; absent when there is none. EU law caps most at six months. */
	probationUntil: contractDateSchema.optional(),
	/** Days of paid leave a year; absent when the contract leaves it to the law or a collective agreement. */
	leaveDays: leaveDaysSchema.optional(),
};

/** What only a contractor agreement carries: whom the contractor invoices through, and when they are paid. */
const contractorFields = {
	kind: z.literal("contractor"),
	/** The business that invoices, when it is not the person themselves. */
	company: z.string().trim().min(1, "Enter a company").max(160, "Use at most 160 characters").optional(),
	/** Its VAT or tax registration number, as its invoices carry it. */
	companyTaxId: z.string().trim().min(1, "Enter a tax or VAT ID").max(40, "Use at most 40 characters").optional(),
	paymentTerms: paymentTermsSchema,
};

/** The one rule a contract's dates follow, on its request and wherever a form checks it too. */
export const contractEndsBeforeItStarts = {
	path: ["endsOn"],
	message: "The contract ends before it takes effect",
};

/**
 * What the person page sends to add or to change a contract, by kind. Add and change carry the same fields: there is
 * nothing the API owns that a client could send by mistake. A fixed-term employment contract must say when it ends;
 * an open-ended one's end date is left out of the record (`personContractFrom`), whatever the request said.
 */
export const personContractRequestSchema = z
	.discriminatedUnion("kind", [
		z.object({ ...contractFields, ...employmentFields, pay: paySchema }),
		z.object({ ...contractFields, ...contractorFields, pay: paySchema }),
	])
	.refine(
		contract =>
			contract.endsOn === undefined ||
			(contract.kind === "employment" && contract.term === "open-ended") ||
			contract.endsOn >= contract.effectiveFrom,
		contractEndsBeforeItStarts,
	)
	.refine(
		contract => contract.kind !== "employment" || contract.term !== "fixed-term" || contract.endsOn !== undefined,
		{
			path: ["endsOn"],
			message: "A fixed-term contract needs an end date",
		},
	)
	.refine(
		contract =>
			contract.kind !== "employment" ||
			contract.probationUntil === undefined ||
			contract.probationUntil >= contract.effectiveFrom,
		{ path: ["probationUntil"], message: "The probation ends before the contract takes effect" },
	);
export type PersonContractRequest = z.infer<typeof personContractRequestSchema>;

/** What the API owns on a contract: its id and when it was added. */
const contractRecordFields = {
	id: z.string().min(1).max(64),
	/**
	 * What it pays. Every contract has pay; it is absent only from what a reader of People & HR is sent
	 * (`contractForReader`), since only an editor ever sees it.
	 */
	pay: paySchema.optional(),
	/** When the contract was added, as an ISO 8601 timestamp in UTC. */
	addedAt: z.iso.datetime(),
};

/** A contract as a person's record keeps it. */
export const personContractSchema = z.discriminatedUnion("kind", [
	z.object({ ...contractFields, ...employmentFields, ...contractRecordFields }),
	z.object({ ...contractFields, ...contractorFields, ...contractRecordFields }),
]);
export type PersonContract = z.infer<typeof personContractSchema>;

/**
 * The contract an add or a change leaves behind: the id and the day it was added are the API's, and everything else is
 * what the request carried — an open-ended employment contract without an end date, whatever the request said. The
 * three contract routes and the frontend's fake API all build a contract this way. A parsed request carries no key
 * for a field nobody gave, so spreading it never writes an undefined attribute.
 */
export function personContractFrom(id: string, addedAt: string, request: PersonContractRequest): PersonContract {
	if (request.kind === "employment" && request.term === "open-ended") {
		const { endsOn: _endsOn, ...terms } = request;
		return { id, ...terms, addedAt };
	}
	return { id, ...request, addedAt };
}

/** A contract as a reader of People & HR is sent it: everything but what it pays. */
export function contractForReader(contract: PersonContract): PersonContract {
	const { pay: _pay, ...seen } = contract;
	return seen;
}

/** Contracts newest first: by the day they take effect, then the one added later, so every read orders them alike. */
export function contractsByDate(contracts: readonly PersonContract[]): PersonContract[] {
	return contracts.toSorted(
		(a, b) =>
			b.effectiveFrom.localeCompare(a.effectiveFrom) ||
			b.addedAt.localeCompare(a.addedAt) ||
			a.id.localeCompare(b.id),
	);
}

/** The contract in effect on `today` (`YYYY-MM-DD`): the newest one whose day has come, or undefined before any has. */
export function currentContract(contracts: readonly PersonContract[], today: string): PersonContract | undefined {
	return contractsByDate(contracts).find(contract => contract.effectiveFrom <= today);
}

/** Where a contract stands among a person's contracts. It is never stored: `contractStanding` works it out. */
export const CONTRACT_STANDINGS = ["scheduled", "current", "ended", "earlier"] as const;
export type ContractStanding = (typeof CONTRACT_STANDINGS)[number];

export const CONTRACT_STANDING_LABELS: Readonly<Record<ContractStanding, string>> = {
	scheduled: "Scheduled",
	current: "Current",
	ended: "Ended",
	earlier: "Earlier",
};

/** Whose contracts they are, as far as a contract's standing goes: the whole list, and the person's own last day. */
export interface ContractHolder {
	readonly contracts?: readonly PersonContract[];
	readonly endDate?: string;
}

/**
 * Where `contract` stands on `today` among its holder's contracts: scheduled until its day comes; then current while it
 * is the newest in effect — or ended, once its own last day or the person's has passed; and earlier once a newer one
 * has taken effect.
 */
export function contractStanding(contract: PersonContract, holder: ContractHolder, today: string): ContractStanding {
	if (contract.effectiveFrom > today) {
		return "scheduled";
	}
	if (currentContract(holder.contracts ?? [], today)?.id !== contract.id) {
		return "earlier";
	}
	const ended = [contract.endsOn, holder.endDate].some(last => last !== undefined && last < today);
	return ended ? "ended" : "current";
}

/** "EUR 4,200 a month", "GBP 450 a day", "EUR 38.50 an hour": the currency's code, as the app shows money. */
export function payText(pay: Pay): string {
	const decimals = Number.isSafeInteger(pay.amount) ? 0 : 2;
	const amount = new Intl.NumberFormat("en-US", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	}).format(pay.amount);
	return `${pay.currency} ${amount} ${PAY_UNIT_PHRASES[pay.unit]}`;
}

/** What the audit log says instead of a value a contract does not give. */
const NOT_GIVEN = "—";

/** What the audit log says for a notice period the contract leaves to the law. */
const STATUTORY_NOTICE = "As the law sets";

/** A number of days as a value reads: "20 days", "1 day". */
function daysText(days: number): string {
	return days === 1 ? "1 day" : `${days} days`;
}

/**
 * A contract's values, in form order and under the form's labels, for the audit log. Pay is quoted: only owners and
 * admins read the audit log, and they see pay anyway.
 */
export function describeNewContract(contract: PersonContract): AuditChange[] {
	const head: AuditChange[] = [
		{ label: "Contract", value: CONTRACT_KIND_LABELS[contract.kind] },
		{ label: "In effect from", value: contract.effectiveFrom },
		...(contract.kind === "employment" ? [{ label: "Term", value: CONTRACT_TERM_LABELS[contract.term] }] : []),
		{ label: "Ends on", value: contract.endsOn ?? NOT_GIVEN },
	];
	const tail: AuditChange[] = [
		{ label: "Workplace", value: WORKPLACE_LABELS[contract.workplace] },
		{
			label: "Notice period",
			value: contract.noticeDays === undefined ? STATUTORY_NOTICE : daysText(contract.noticeDays),
		},
		{ label: "Pay", value: contract.pay === undefined ? NOT_GIVEN : payText(contract.pay) },
	];
	if (contract.kind === "employment") {
		return [
			...head,
			{ label: "Working time", value: WORKING_TIME_LABELS[contract.workingTime] },
			{ label: "Hours a week", value: String(contract.weeklyHours) },
			{ label: "Probation until", value: contract.probationUntil ?? NOT_GIVEN },
			{
				label: "Paid leave",
				value: contract.leaveDays === undefined ? NOT_GIVEN : `${daysText(contract.leaveDays)} a year`,
			},
			...tail,
		];
	}
	return [
		...head,
		...tail,
		{ label: "Company", value: contract.company ?? NOT_GIVEN },
		{ label: "Tax or VAT ID", value: contract.companyTaxId ?? NOT_GIVEN },
		{ label: "Payment terms", value: PAYMENT_TERM_LABELS[contract.paymentTerms] },
	];
}

/**
 * What changed between two versions of a contract, newest value first, in form order: every value either version
 * gives that differs, and a dash for one the other kind of contract does not have.
 */
export function describeContractChanges(before: PersonContract, after: PersonContract): AuditChange[] {
	const previous = new Map(describeNewContract(before).map(change => [change.label, change.value]));
	const changes: AuditChange[] = [];
	for (const { label, value } of describeNewContract(after)) {
		const was = previous.get(label) ?? NOT_GIVEN;
		previous.delete(label);
		if (was !== value) {
			changes.push({ label, value, previous: was });
		}
	}
	for (const [label, was] of previous) {
		changes.push({ label, value: NOT_GIVEN, previous: was });
	}
	return changes;
}

/** "Ida P. · Employment contract from 2026-10-08": whose contract, of what kind, and from when. */
function contractDetail(personName: string, contract: PersonContract): string {
	return `${personName} · ${CONTRACT_KIND_LABELS[contract.kind]} from ${contract.effectiveFrom}`;
}

/** The audit entry for a contract added to a person's record. */
export function contractAddedAudit(personName: string, contract: PersonContract): { action: string; detail: string } {
	return { action: "Added contract", detail: contractDetail(personName, contract) };
}

/** The audit entry for a contract that changed: its detail, then what changed, as a person's update does. */
export function contractUpdatedAudit(
	personName: string,
	contract: PersonContract,
	changes: readonly AuditChange[],
): { action: string; detail: string } {
	return {
		action: "Updated contract",
		detail: [
			contractDetail(personName, contract),
			...changes.map(change => `${change.label}: ${change.value}`),
		].join(" · "),
	};
}

/** The audit entry for a contract taken off a person's record; it names the contract, since its terms are gone. */
export function contractRemovedAudit(personName: string, contract: PersonContract): { action: string; detail: string } {
	return { action: "Removed contract", detail: contractDetail(personName, contract) };
}
